/** @format */

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Transition } from "@headlessui/react";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useUser } from "@/api/userAPI";
import { useCTAControl } from "@/hooks/useCTAControl";
import { useStableFeatureFlags } from "@/hooks/useStableFeatureFlags";
import { faCheck, faUserPlus, faXmark } from "@/utils/iconLibraryTreeShake";
import { safeStorage } from "@/utils/storage";
import { getVisitCount } from "@/utils/visitCounter";

const DISMISS_KEY = "signup-banner-dismissed";
const DISMISS_DURATION_MS = 14 * 24 * 60 * 60 * 1000; // 14 days
const MIN_VISITS = 3;

function wasDismissedRecently(): boolean {
  const dismissed = safeStorage.getItem(DISMISS_KEY);
  if (!dismissed) return false;
  return Date.now() - Number(dismissed) < DISMISS_DURATION_MS;
}

const SETTLE_DELAY_MS = 2000;

const SignUpBanner = () => {
  const { data: user, isLoading } = useUser();
  const flags = useStableFeatureFlags();
  const navigate = useNavigate();
  const notificationsEnabled = flags?.includes("notifications") ?? false;

  // Defer eligibility so the banner doesn't flash while the page is still loading
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setSettled(true), SETTLE_DELAY_MS);
    return () => clearTimeout(id);
  }, []);

  const isEligible =
    settled &&
    !isLoading &&
    !user?.id &&
    getVisitCount() >= MIN_VISITS &&
    !wasDismissedRecently();
  const { isActive, dismiss, trackClick } = useCTAControl("sign-up", {
    experimentKey: "cta-signup-banner",
    isEligible,
  });

  const handleDismiss = () => {
    safeStorage.setItem(DISMISS_KEY, String(Date.now()));
    dismiss();
  };

  const handleSignUp = () => {
    trackClick();
    safeStorage.setItem(DISMISS_KEY, String(Date.now()));
    dismiss();
    void navigate({ to: "/", search: { modal: "register" }, replace: true });
  };

  return (
    <Transition
      as="div"
      show={isActive}
      className="fixed bottom-4 right-4 z-50"
      enter="duration-500 ease-in-out transition-transform"
      enterFrom="translate-y-36"
      enterTo="translate-y-0"
      leave="duration-500 ease-in-out transition-transform"
      leaveFrom="translate-y-0"
      leaveTo="translate-y-36"
    >
      <div className="w-80 bg-slate-900 rounded-md px-4 py-3 shadow-lg border border-slate-700">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-violet-600">
              <FontAwesomeIcon icon={faUserPlus} className="text-white text-sm" />
            </div>
            <p className="text-base font-semibold text-white">Create a Free Account</p>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            className="text-slate-400 hover:text-slate-200 p-1 -m-1"
            aria-label="Dismiss"
          >
            <FontAwesomeIcon icon={faXmark} className="text-sm" />
          </button>
        </div>

        <ul className="mt-2.5 space-y-1.5">
          <BenefitItem text="Track your favorite drivers across every race" />
          <BenefitItem text="Save custom dashboard layouts" />
          {notificationsEnabled && (
            <BenefitItem text="Get push notifications for race events" />
          )}
        </ul>

        <div className="flex items-center justify-between mt-3 gap-x-2">
          <button
            type="button"
            onClick={handleDismiss}
            className="text-slate-400 hover:text-slate-200 rounded bg-slate-700 px-3 py-1.5 text-sm"
          >
            Not now
          </button>
          <button
            type="button"
            onClick={handleSignUp}
            className="flex-1 rounded bg-violet-600 hover:bg-violet-500 px-3 py-1.5 text-sm font-medium text-white"
          >
            Sign Up Free
          </button>
        </div>
      </div>
    </Transition>
  );
};

const BenefitItem = ({ text }: { text: string }) => (
  <li className="flex items-start gap-2 text-sm text-slate-300">
    <FontAwesomeIcon
      icon={faCheck}
      className="mt-0.5 text-violet-400 text-xs shrink-0"
    />
    {text}
  </li>
);

export default SignUpBanner;
